import { Injectable, Inject, HttpStatus } from '@nestjs/common';
import { Repository, Raw, getConnection, LessThan, MoreThan } from 'typeorm';
import { InjectRepository } from '@nestjs/typeorm';
import { RolesEntity } from './roles.entity';
import { RolesDTO, RoleStatusUpdateDto, RoleUpdateDto } from './dto/roles.dto';
import { RolesResponseModel } from './dto/roles-response.model';
import { ErrorResponse } from '@gtpl/shared-models/common-models';
import { AllRolesResponseModel } from './dto/all-roles-response.model';

@Injectable()
export class RolesService{
    constructor(
        @InjectRepository(RolesEntity)
        private rolesRespository: Repository<RolesEntity>,
    ){}

    async createRole(rolesDto: RolesDTO, request:Request): Promise<RolesResponseModel> {
        const response = new RolesResponseModel();
        try {
            const roleExists = await this.getRoleWithoutRelations(rolesDto.roleName);
            if(roleExists) {
                throw new ErrorResponse(11104, 'Role already exists');
            } else {
                const savedRole = await this.rolesRespository.save(rolesDto);
                if(savedRole) {
                    response.status = true;
                    response.data   = savedRole;
                    response.statusCode = 50000;
                    response.internalMessage = 'Role created successfully';
                    response.intlCode = 11100;
                } else { 
                    throw new ErrorResponse(11106, 'Role not created')
                }
            } 
            return response;
        }
        catch (error){
            throw error;
        }
    }

    async getRoleWithoutRelations(roleName: string): Promise<RolesEntity> {
        const roleResponse = await this.rolesRespository.findOne({
            where: {roleName: Raw(alias => `UPPER(role_name) = '${roleName.toLocaleUpperCase()}'`), status: true},
        });
        if(roleResponse) {
            return roleResponse;
        } else {
            return null;
        }
    }

    async getRoleByRoleId(roleId: number): Promise<RolesEntity> {
        const roleResponse = await this.rolesRespository.findOne({
            where: {Id:roleId},
        });
        if(roleResponse) {
            return roleResponse;
        } else {
            return null;
        }
    }

    async getActiveRoles(request:Request):Promise<AllRolesResponseModel> {
        const response = new AllRolesResponseModel();
        try {
            const activeRoles = await this.rolesRespository.find({
                where: {status: true},
                order: { createdAt: 'DESC' }
            });
            if(activeRoles) {
                response.status = true;
                response.data = activeRoles;
                response.statusCode = 50000;
                response.internalMessage = 'Data retrieved successfully';
                response.intlCode = 11108;
            } else {
                throw new ErrorResponse(99998, 'Data not found');
            }
            return response;
        } catch (error) {
            throw error;
        }
    }

    async updateRole(roleUpdateDto: RoleUpdateDto, request: Request):Promise<RolesResponseModel> {
        const response = new RolesResponseModel();
        try {
            const updateRole = await this.rolesRespository.update(roleUpdateDto.Id, roleUpdateDto);
            const updatedData = await this.rolesRespository.findOne({
                where:{Id:roleUpdateDto.Id}
            });
            if(updateRole) {
                response.status = true;
                response.data   = updatedData;
                response.statusCode = 50000;
                response.internalMessage = 'Role updated successfully';
                response.intlCode = 11108;
            } else {
                throw new ErrorResponse(99998, 'Role update failed')
            }
            return response;
        } catch (error) {
            throw error;
        }
    }

    async updateRoleStatus(roleStatusUpdateDto:RoleStatusUpdateDto, request:Request):Promise<RolesResponseModel> {
        const response = new RolesResponseModel();
        try {
            const updateRole = await this.rolesRespository.update(roleStatusUpdateDto.Id, roleStatusUpdateDto);
            const updatedData = await this.rolesRespository.findOne({
                where:{Id:roleStatusUpdateDto.Id}
            });
            if(updateRole) {
                response.status = true;
                response.data   = updatedData;
                response.statusCode = 50000;
                response.internalMessage = 'Role Status updated successfully';
                response.intlCode = 11108;
            } else {
                throw new ErrorResponse(99998, 'Role Status update failed')
            }
            return response;
        } catch (error) {
            throw error;
        }
    }

    async getAllRolesByLevel(queryParams, request:Request):Promise<AllRolesResponseModel> {
        const response = new AllRolesResponseModel();
        try {
            const roleId = request.headers['roleid']
            const roleData = await this.getRoleByRoleId(roleId);
            if(queryParams.apiVersion == 1) {
                if(roleData) { 
                    const activeRoles = await this.rolesRespository.find({
                        where: {level:MoreThan(roleData.level),status: true},
                        order: { createdAt: 'DESC' },
                        select:['Id','roleName']
                    });
                    if(activeRoles) {
                        response.status = true;
                        response.data = activeRoles;
                        response.statusCode = 50000;
                        response.internalMessage = 'Data retrieved successfully';
                        response.intlCode = 11108;
                    } else {
                        throw new ErrorResponse(99998, 'Data not found');
                    }
                    return response;
                } else {
                    throw new ErrorResponse(99998, 'Data not found');
                }
            } else {
                if(roleData) { 
                    const activeRoles = await this.rolesRespository.find({
                        where: {level:MoreThan(roleData.level),status: true},
                        order: { createdAt: 'DESC' },
                        select:['Id','roleName']
                    });
                    if(activeRoles) {
                        response.status = true;
                        response.data = activeRoles;
                        response.statusCode = 50000;
                        response.internalMessage = 'Data retrieved successfully';
                        response.intlCode = 11108;
                    } else {
                        throw new ErrorResponse(99998, 'Data not found');
                    }
                    return response;
                } else {
                    throw new ErrorResponse(99998, 'Data not found');
                }
            }
        } catch (error) {
            throw error;
        }
    }
}